import { useEffect, useMemo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Save, Undo2 } from 'lucide';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';

import Icon from 'common/components/Icon';
import { isClientOrgType } from 'enum/combobox';
import { shouldShowCustomerList } from 'modules/account/detail/helpers/utils';
import CustomersListHeading from 'modules/account/components/CustomersListHeading';
import { formatRolesData, Roles } from 'modules/account/services/useGetRoles';
import CustomersForCQQL from 'modules/account/components/customers/CustomersForCQQL';
import FullCustomersList from 'modules/account/components/customers/FullCustomersList';
import { useTabsData } from 'modules/account/detail/hooks/useTabsData';
import RolesList from 'modules/account/detail/tabs/RolesList';
import { useRolesAssignmentHandlers } from 'modules/account/detail/tabs/useRolesAssignmentHandlers';

const RolesAssignmentTab = () => {
  const {
    userInfo,
    unitAddress,
    selectedRole,
    rolesToAssign,
    isCQQL,
    accountGovRoles,
    viewOnly,
    hvsInfo,
    isSaving,
    setRolesToAssign,
    handleUpdate,
    toPreviousPage,
  } = useTabsData();

  const { rolesAssignment, allRoles, customersSource } = rolesToAssign ?? {};
  const forGovAgencyBusiness = rolesAssignment?.forGovAgencyBusiness ?? [];
  const unitId = String(userInfo.unitId);
  const [roles, setRoles] = useState<Partial<Roles>>({
    businessRoles: [],
    adminRoles: [],
    systemRoles: [],
  });
  const { formatMessage } = useIntl();

  const customerIdsByLocality = useMemo(
    () => computeCustomerIdsByLocality(customersSource ?? [], unitAddress),
    [customersSource, unitAddress]
  );
  // Xét theo LOẠI đơn vị, không cắt tiền tố mã CĐS (mã khách hàng giờ là `HSKD-*` nên so với 'KH'
  // luôn sai) — xem isClientOrgType.
  const isClientUser = useMemo(
    () => isClientOrgType(userInfo.userAuthen?.userActualManagingOrgType),
    [userInfo.userAuthen]
  );
  const shouldShowCustomersForCQQL = useMemo(
    () => Boolean(isCQQL && selectedRole?.rolesType === 'businessRoles'),
    [selectedRole, isCQQL]
  );
  const filteredCustomers = useMemo(
    () =>
      isClientUser
        ? (customersSource ?? []).filter(
            (c) => c.operationCode === userInfo.userAuthen?.userActualManagingOrgId
          )
        : (customersSource ?? []),
    [customersSource, isClientUser, userInfo.userAuthen?.userActualManagingOrgId]
  );

  const {
    handleSelectSystemRole,
    handleSelectCustomersByLocality,
    handleToggleRoleFromList,
    utilFunctions,
  } = useRolesAssignmentHandlers({
    selectedRole,
    setRolesToAssign,
    unitId,
    isCQQL: isCQQL ?? false,
    forGovAgencyBusiness,
    unitAddress,
    accountGovRoles: accountGovRoles ?? [],
    hvsCompanyOperationCode: hvsInfo?.company?.operationCode ?? '',
    customerIdsByLocality,
  });

  useEffect(() => {
    if (!allRoles) return;
    setRoles(formatRolesData(!!isCQQL, allRoles));
  }, [allRoles, isCQQL]);

  if (!rolesToAssign || !allRoles) return null;

  return (
    <Grid container spacing={2} sx={{ paddingLeft: '16px' }}>
      <Grid item xs={6} sx={{ minHeight: '60dvh' }}>
        <RolesList
          roles={roles}
          onSelectSystemRole={handleSelectSystemRole}
          onToggleRoleFromList={handleToggleRoleFromList}
        />
      </Grid>
      <Grid item xs={6} sx={{ minHeight: '60dvh', maxHeight: '70vh', overflow: 'scroll' }}>
        {shouldShowCustomerList(selectedRole) && (
          <>
            <CustomersListHeading label={formatMessage({ id: 'account.customersList' })} />
            {shouldShowCustomersForCQQL ? (
              <CustomersForCQQL
                unitAddress={unitAddress}
                isCQQL={isCQQL}
                rolesToAssign={rolesToAssign}
                currentSelectedRole={selectedRole}
                onSelectCustomersByLocality={handleSelectCustomersByLocality}
                utilFunctions={utilFunctions}
                viewOnly={viewOnly}
              />
            ) : (
              <FullCustomersList
                currentSelectedRole={selectedRole}
                customers={filteredCustomers}
                rolesToAssign={rolesToAssign}
                utilFunctions={utilFunctions}
                isCQQL={isCQQL}
                viewOnly={viewOnly}
              />
            )}
          </>
        )}
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
        <Button
          variant="outlined"
          startIcon={<Icon icon={Undo2} size={16} />}
          sx={{ marginRight: 2 }}
          onClick={toPreviousPage}
          disabled={isSaving}
        >
          <FormattedMessage id="back" />
        </Button>
        {!viewOnly && (
          <LoadingButton
            variant="contained"
            startIcon={<Icon icon={Save} size={16} />}
            onClick={handleUpdate}
            loading={isSaving}
          >
            <FormattedMessage id="save" />
          </LoadingButton>
        )}
      </Box>
    </Grid>
  );
};

export default RolesAssignmentTab;

function computeCustomerIdsByLocality(
  customersSource: Array<{ wardName?: string; operationCode: string }>,
  unitAddress?: { ward?: string }
): string[] {
  if (!unitAddress?.ward) return [];
  return customersSource.filter((c) => c.wardName === unitAddress.ward).map((c) => c.operationCode);
}
